import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { TaskUnitsService } from 'src/app/tasks/task/units/task-units.service';
import { CompanySettingsService } from 'src/app/services/company-settings.service';
import { colors } from './colors';
import { TaskUnit } from 'src/app/tasks/task/units/models/task-unit';

interface ChartData {
    category: string,
    translatedCategory?: string,
    value: number,
    percent?: number,
    color: string
}

@Component({
    selector: 'app-units-dashboard',
    templateUrl: './units-dashboard.component.html',
    styleUrls: ['./units-dashboard.component.scss']
})
export class UnitsDashboardComponent implements OnInit {
    public taskId = '';

    public loading: boolean = true;
    public subscriptions: Array<Subscription> = [];

    public invoicingProposals;
    public unitLists = [];
    private listUnits: TaskUnit[] = [];
    private openUnits: TaskUnit[] = [];
    public displayedColumns = ['name', 'ordered', 'supplied', 'invoiced', 'aprice', 'amounts', 'invoicingForSupplied'];
    public units = [];
    public budgetUnitList = [];

    public selectedUnitList: any = 0;
    public selectedPriceListName = '-';
    public totalChartCategories = ['Ordered', 'Supplied', 'Invoiced'];
    public totalChartData: ChartData[] = [];
    public invoicedChartValues: ChartData[] = [];
    public invoicedChartPercent: number = 0;

    constructor(private dialogRef: MatDialogRef<UnitsDashboardComponent>,
        @Inject(MAT_DIALOG_DATA) private dialogData: any,
        private readonly taskUnitsService: TaskUnitsService,
        private readonly companySettingsService: CompanySettingsService) { }

    ngOnInit(): void {
        this.taskId = this.dialogData.taskId;

        this.subscriptions.push(
            this.taskUnitsService.getUnitLists(this.taskId).subscribe({
                next: (res) => {
                    this.unitLists = res.filter(list => list.Type === 'TradingUnitList');
                    this.budgetUnitList = res.filter(list => list.Type !== 'TradingUnitList').map(i => i.Id);
                }
            })
        )

        this.subscriptions.push(
            this.taskUnitsService.getInvoicingProposals(this.taskId)
                .subscribe({
                    next: res => {
                        this.invoicingProposals = res.value;
                    },
                    complete: () => {
                        this.subscriptions.push(
                            this.getUnits(this.taskId).subscribe(res => {
                                this.listUnits = res[0]['value'].filter(unit => !this.budgetUnitList.includes(unit.UnitListId));
                                this.openUnits = res[1]['value'].filter(unit => !this.budgetUnitList.includes(unit.UnitListId));
                                if (res[2]) {
                                    this.listUnits = this.sortUnits(this.listUnits, true);
                                    this.openUnits = this.sortUnits(this.openUnits, true);
                                }
                                this.listUnits.forEach(listUnit => listUnit['subheading'] = 'listUnits');
                                this.openUnits.forEach(openUnit => openUnit['subheading'] = 'openUnits');
                                this.setUnits();
                            }));
                    }
                }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    closeDialog() {
        this.dialogRef.close();
    }

    public getUnits(taskId: string): Observable<any[]> {
        const res1 = this.taskUnitsService.getTaskListUnits(taskId);
        const res2 = this.taskUnitsService.getTaskOpenUnits(taskId);
        const res3 = this.companySettingsService.getUnitSortOrderSettingStatus(this.taskId)
        return forkJoin([res1, res2, res3]);
    }

    private sortUnits(units: TaskUnit[], isAsc: boolean) {
        return units.sort((a, b) => {
            return (new Date(a.Created).getTime() - new Date(b.Created).getTime()) * (isAsc ? 1 : -1);
        });
    }

    public setUnits() {
        if (this.selectedUnitList) {
            this.units = [...this.listUnits.filter(u => u.UnitListId === this.selectedUnitList.Id), ...this.openUnits.filter(u => u.UnitListId === this.selectedUnitList.Id)];
        } else {
            this.units = [...this.listUnits, ...this.openUnits];
        }

        this.loading = false;
        this.updateTotalChartData();
        this.updateInvoicedChartValues();

    }

    public updateTotalChartData() {
        this.totalChartData = this.totalChartCategories.map(category => this.getCategoryData(category));
    }

    public updateInvoicedChartValues() {
        let suppliedTotalValue = this.getTotalValues('Supplied');

        let invoiced = this.getCategoryData('Invoiced');
        invoiced['percent'] = invoiced.value * 100 / suppliedTotalValue;

        let invoicedUnapproved = this.getCategoryData('InvoicedUnapproved');
        invoicedUnapproved['percent'] = invoicedUnapproved.value * 100 / suppliedTotalValue;

        let notInvoiced = this.getCategoryData('NotInvoiced');
        notInvoiced['percent'] = notInvoiced.value * 100 / suppliedTotalValue;

        this.invoicedChartValues = [invoiced, invoicedUnapproved, notInvoiced];
        this.invoicedChartPercent = +(invoiced['percent']).toFixed(2);
    }

    public getCategoryData(category) {
        return {
            category: category,
            value: this.getTotalValues(category),
            color: colors.find(c => c.category === category)['color']
        }
    }

    public getTotalValues(category) {
        if (this.selectedUnitList) {
            return this.selectedUnitList[category + 'Value'];
        } else {
            return this.unitLists.map(unitList => unitList[category + 'Value']).reduce((acc, value) => acc + value, 0) || 0;
        }
    }

    public unitListChanges() {
        this.selectedPriceListName = this.selectedUnitList ? this.selectedUnitList.PriceListName : '-';
        this.setUnits();
    }

    public compareUnitLists(option, value): boolean {
        return option.id === value.id;
    }

    public isSubheading = (index: number, item: any): boolean => {
        return index === 0 || this.units[index - 1].subheading !== item.subheading;
    };

    public getAmountBarStyles(unit, category) {
        let values = [unit['OrderedTotalValue'], unit['SuppliedTotalValue'], unit['InvoicedTotalValue']]
        let maxValue = Math.max(...values);
        let width = ((unit[category + 'TotalValue'] / maxValue) * 100).toFixed(2);

        return { width: `${width}%`, background: colors.find(c => c.category === category)['color'] }
    }
}
